import React, {useEffect, useState} from 'react';
import {useTranslation} from "react-i18next";
import {IError} from "../../../../../app/interfaces/response/IResponse";
import {useShipmentApi} from "../../../../../app/api/shipment";
import {toast} from "react-toastify";
import {ResponseResultCode} from "../../../../../app/enums/ResponseResultCode";
import ToastLayout from "../../../../../components/uiLayouts/ToastLayout/ToastLayout";
import {isAxiosError} from "axios";
import FormLoadingScreen from "../../../../../components/ui/loaders/FormLoadingScreen/FormLoadingScreen";
import {IEditTransportGetAction} from "../../../../../app/interfaces/shipment/IEditTransportGetAction";
import {IEditTransportPostAction} from "../../../../../app/interfaces/shipment/IEditTransportPostAction";
import Button, {ButtonType} from "../../../../../components/ui/Button/Button";
import EditTransportForm from "./EditTransportForm/EditTransportForm";
import Alert from "../../../../../components/ui/Alert/Alert";
import { BLErrorCode } from '../../../../../app/enums/BLErrorCode';

type EditTransportProps = {
    id: string;
    sId: string;
    close?: () => void;
}

const EditTransport: React.FC<EditTransportProps> = ({
                                                         id,
                                                         sId,
                                                         close
                                                     }) => {
    const {t} = useTranslation();
    const [disabled, setDisabled] = useState<boolean>(false);
    const [model, setModel] = useState<IEditTransportGetAction | null>(null);
    const [errors, setErrors] = useState<Array<IError>>([]);

    const {
        getEditTransportData: {
            query: getEditTransportData,
            isLoading,
            cancel: cancelGetEditTransportData
        },
        editTransportData: {
            mutation: editTransportData,
            cancel: cancelEditTransportData
        }
    } = useShipmentApi();

    useEffect(() => {
        if (id === '') {
            if (close) {
                close()
            } else {
                return;
            }
        }

        (async () => {
            try {
                const response = await getEditTransportData(id, sId);
                if (response?.status === 200 && response.data && response.data.data && response.data.data) {
                    setModel(response.data.data);
                }
            } catch {
                if (close) {
                    close();
                } else {
                    toast.error<string>('Unable to get change ref number data');
                }
            }
        })();

        return () => {
            cancelEditTransportData();
            cancelGetEditTransportData();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const save = async (model: IEditTransportPostAction): Promise<void> => {
        setDisabled(prev => !prev);
        setErrors([]);

        try {
            const response = await editTransportData(model);

            if (response?.status === 200 && response.data.resultCode === ResponseResultCode.Ok) {
                if (close) {
                    close();
                }

                document.dispatchEvent(new CustomEvent('onGridMessage', {
                    detail: {
                        action: 'updateRows',
                        rows: [
                            {
                                id: sId,
                                isRowLoading: true
                            }
                        ]
                    }
                }));

                toast.success(<ToastLayout text={t("shared.labels.delayed-update-warning")}
                                           title={`${t("shared.labels.shipment")} ${sId}`}/>);
            }
        } catch (err) {
            if (isAxiosError(err)) {
                if (err.response && err.response.data && err.response.status === 422 &&
                    err.response.data.resultCode === ResponseResultCode.NotValidData &&
                    err.response.data.errors && Array.isArray(err.response.data.errors)) {
                    setErrors(err.response.data.errors);
                } else if (close) {
                    close();
                    toast.error(err.message);
                }
            }
        }

        setDisabled(prev => !prev);
    }

    if (isLoading || model === null) {
        return (
            <FormLoadingScreen height={`200px`} style={{padding: '5px'}}/>
        )
    }

    return (
        <div className={'w100'}>
            <div className={'dialog-default-content-container'}>
                {model.isLocked
                    ? <Alert type={'Error'}
                             id={'edit-transport-locked-error'}
                             style={{
                                 textAlign: 'center',
                                 fontSize: '14px',
                                 fontWeight: 'bold',
                                 padding: '5px'
                             }}>
                        {t("shipment.errors.edit-transport-is-locked")}
                    </Alert>
                    : <EditTransportForm model={model}
                                         save={save}
                                         disabled={disabled}
                                         errors={errors}/>
                }
            </div>
            <div className={'dialog-default-buttons-container'}>
                {close &&
                    <Button buttonType={ButtonType.Transparent}
                            disabled={disabled}
                            className={'close-button'}
                            onClick={close}>
                        {t("shared.btn.cancel")}
                    </Button>
                }
                {!model.isLocked &&
                    <Button buttonType={ButtonType.Primary}
                            disabled={disabled}
                            form='edit-transport-form'
                            type='submit'
                            style={{minWidth: '150px'}}
                            className={'confirm-button'}
                            isLoading={disabled}>
                        {t("shared.btn.send")}
                    </Button>
                }
            </div>
        </div>
    );
};

export default EditTransport;
